import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth, getLandingPath } from "./lib/auth";
import { Toaster } from "sonner";
import axios from "axios";
import { detectTenantSlug, applyTenantBranding } from "./lib/subdomain";
import { TenantNotConfigured } from "./components/TenantNotConfigured";

// Layout
import { DashboardLayout } from "./components/layout/DashboardLayout";

// Pages
import { LoginPage } from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import { DashboardPage } from "./pages/DashboardPage";
import { HospitalsPage } from "./pages/HospitalsPage";
import { UsersPage } from "./pages/UsersPage";
import { RolesPage } from "./pages/RolesPage";
import PlatformHealthPage from "./pages/PlatformHealthPage";
import ProductionCleanupPage from "./pages/ProductionCleanupPage";
import BulkStaffImportPage from "./pages/BulkStaffImportPage";
import PharmacyPage from "./pages/PharmacyPage";
import PrescriptionsPage from "./pages/PrescriptionsPage";
import LabPage from "./pages/LabPage";
import ImagingPage from "./pages/ImagingPage";
import InsurancePage from "./pages/InsurancePage";
import BloodBankPage from "./pages/BloodBankPage";
import StorePage from "./pages/StorePage";
import MortuaryPage from "./pages/MortuaryPage";
import CanteenPage from "./pages/CanteenPage";
import DepartmentsPage from "./pages/DepartmentsPage";
import { SubscriptionsPage } from "./pages/SubscriptionsPage";
import AuditLogPage from "./pages/AuditLogPage";
import { SettingsPage } from "./pages/SettingsPage";
import { BrandingPage } from "./pages/BrandingPage";
import StaffPage from "./pages/StaffPage";
import AttendancePage from "./pages/AttendancePage";
import MyAttendancePage from "./pages/MyAttendancePage";
import MyProfilePage from "./pages/MyProfilePage";
import PublicHolidaysPage from "./pages/PublicHolidaysPage";
import KYCCompliancePage from "./pages/KYCCompliancePage";
import CelebrationSettingsPage from "./pages/CelebrationSettingsPage";
import IPDReferralsInboxPage from "./pages/IPDReferralsInboxPage";
import FeedbackDashboardPage from "./pages/FeedbackDashboardPage";
import FinanceDashboardPage from "./pages/FinanceDashboardPage";
import Stats360Page from "./pages/Stats360Page";
import Staff360Page from "./pages/Staff360Page";
import Staff360PickerPage from "./pages/Staff360PickerPage";
import DepartmentDashboardPage from "./pages/DepartmentDashboardPage";
import PartnerKeysPage from "./pages/PartnerKeysPage";
import InsuranceClaimPrint from "./pages/InsuranceClaimPrint";
import DischargeSummaryPrint from "./pages/DischargeSummaryPrint";
import LabOrderPrint from "./pages/LabOrderPrint";
import PharmacyDispensePrint from "./pages/PharmacyDispensePrint";
import PaymentReceiptPrint from "./pages/PaymentReceiptPrint";
import LeavePage from "./pages/LeavePage";
import { NoticePage } from "./pages/NoticePage";
import NoticeDashboardPage from "./pages/NoticeDashboardPage";
import AccessControlPage from "./pages/AccessControlPage";
import { DoctorConfigPage } from "./pages/DoctorConfigPage";
import { SecurityPage } from "./pages/SecurityPage";
import { PatientsPage } from "./pages/PatientsPage";
import PaymentsPage from "./pages/PaymentsPage";
import InvoiceHistoryPage from "./pages/InvoiceHistoryPage";
import { PatientDetailPage } from "./pages/PatientDetailPage";
import VipDashboardPage from "./pages/VipDashboardPage";
import { AppointmentsPage } from "./pages/AppointmentsPage";
import { QueueDisplayPage } from "./pages/QueueDisplayPage";
import { InvoicesPage } from "./pages/InvoicesPage";
import { WardsPage } from "./pages/WardsPage";
import { AdmissionsPage } from "./pages/AdmissionsPage";
import { BillingConfigPage } from "./pages/BillingConfigPage";
import { RevenuePayoutsPage } from "./pages/RevenuePayoutsPage";
import { VendorsPage } from "./pages/VendorsPage";
import { ExpensesPage } from "./pages/ExpensesPage";
import { ProfitLossPage } from "./pages/ProfitLossPage";
import { FinanceConfigPage } from "./pages/FinanceConfigPage";
import { DoctorDashboardPage } from "./pages/DoctorDashboardPage";
import { PatientDashboardPage } from "./pages/PatientDashboardPage";
import ServiceCatalogPage from "./pages/ServiceCatalogPage";
import BedStatusPage from "./pages/BedStatusPage";
import DischargeSummaryPage from "./pages/DischargeSummaryPage";
import RefundPage from "./pages/RefundPage";
import AllergiesPage from "./pages/AllergiesPage";
import ConsultationTypesPage from "./pages/ConsultationTypesPage";
import DoctorAvailabilityPage from "./pages/DoctorAvailabilityPage";
import VitalsPage from "./pages/VitalsPage";
import RecurringAppointmentsPage from "./pages/RecurringAppointmentsPage";
import NurseDashboardPage from "./pages/NurseDashboardPage";
import RemindersPage from "./pages/RemindersPage";
import PurchaseOrdersPage from "./pages/PurchaseOrdersPage";
import ShiftsPage from "./pages/ShiftsPage";
import PayrollEnhancedPage from "./pages/PayrollEnhancedPage";
import DoctorCalendarPage from "./pages/DoctorCalendarPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";

function RoleLandingRedirect() {
  // v34 — Read the logged-in user's role.default_landing and redirect there.
  // Guards on auth loading so a hard refresh on '/' or a non-matching path doesn't
  // race past the AuthProvider's /auth/me hydration and fall back to /dashboard.
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={getLandingPath(user)} replace />;
}

function Staff360MeRedirect() {
  // v38 — convenience route /my-360 → /staff-360/<own user_id>
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user?.id) return <Navigate to="/login" replace />;
  return <Navigate to={`/staff-360/${user.id}`} replace />;
}

function AppRouter() {
  // CRITICAL: Synchronously detect session_id in URL fragment BEFORE auth checks
  // This prevents race conditions with AuthProvider's /auth/me call.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />

      {/* Print routes — render OUTSIDE DashboardLayout so sidebar/header are hidden when printing */}
      <Route path="/insurance/print/:id" element={<InsuranceClaimPrint />} />
      <Route path="/preauth/print/:id" element={<InsuranceClaimPrint />} />
      <Route path="/discharge/print/:id" element={<DischargeSummaryPrint />} />
      <Route path="/lab/print/:id" element={<LabOrderPrint />} />
      <Route path="/pharmacy/print/:id" element={<PharmacyDispensePrint />} />
      <Route path="/invoice/print/:id" element={<PaymentReceiptPrint />} />
      
      {/* Protected Routes */}
      <Route path="/" element={<DashboardLayout />}>
        <Route index element={<RoleLandingRedirect />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="hospitals" element={<HospitalsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="roles" element={<RolesPage />} />
        <Route path="platform-health" element={<PlatformHealthPage />} />
        <Route path="production-cleanup" element={<ProductionCleanupPage />} />
        <Route path="bulk-staff-import" element={<BulkStaffImportPage />} />
        <Route path="pharmacy" element={<PharmacyPage />} />
        <Route path="prescriptions" element={<PrescriptionsPage />} />
        <Route path="lab" element={<LabPage />} />
        <Route path="imaging" element={<ImagingPage />} />
        <Route path="insurance" element={<InsurancePage />} />
        <Route path="bloodbank" element={<BloodBankPage />} />
        <Route path="store" element={<StorePage />} />
        <Route path="mortuary" element={<MortuaryPage />} />
        <Route path="canteen" element={<CanteenPage />} />
        <Route path="departments" element={<DepartmentsPage />} />
        <Route path="subscriptions" element={<SubscriptionsPage />} />
        <Route path="branding" element={<BrandingPage />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="my-attendance" element={<MyAttendancePage />} />
        <Route path="my-profile" element={<MyProfilePage />} />
        <Route path="public-holidays" element={<PublicHolidaysPage />} />
        <Route path="kyc-compliance" element={<KYCCompliancePage />} />
        <Route path="celebrations-settings" element={<CelebrationSettingsPage />} />
        <Route path="ipd-referrals" element={<IPDReferralsInboxPage />} />
        <Route path="feedback-dashboard" element={<FeedbackDashboardPage />} />
        <Route path="finance-dashboard" element={<FinanceDashboardPage />} />
        <Route path="stats360" element={<Stats360Page />} />
        <Route path="department-dashboard" element={<DepartmentDashboardPage />} />
        <Route path="staff-360" element={<Staff360PickerPage />} />
        <Route path="staff-360/:userId" element={<Staff360Page />} />
        <Route path="my-360" element={<Staff360MeRedirect />} />
        <Route path="integrations/partner-keys" element={<PartnerKeysPage />} />
        <Route path="leave" element={<LeavePage />} />
        <Route path="notices" element={<NoticePage />} />
        <Route path="notice-dashboard" element={<NoticeDashboardPage />} />
        <Route path="access-control" element={<AccessControlPage />} />
        <Route path="doctor-config" element={<DoctorConfigPage />} />
        <Route path="security" element={<SecurityPage />} />
        <Route path="patients" element={<PatientsPage />} />
        <Route path="patients/:patientId" element={<PatientDetailPage />} />
        <Route path="vip-patients" element={<VipDashboardPage />} />
        <Route path="nurse-dashboard" element={<NurseDashboardPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="invoice-history" element={<InvoiceHistoryPage />} />
        <Route path="appointments" element={<AppointmentsPage />} />
        <Route path="queue" element={<QueueDisplayPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="wards" element={<WardsPage />} />
        <Route path="admissions" element={<AdmissionsPage />} />
        <Route path="billing-config" element={<BillingConfigPage />} />
        <Route path="revenue" element={<RevenuePayoutsPage />} />
        <Route path="vendors" element={<VendorsPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="profit-loss" element={<ProfitLossPage />} />
        <Route path="finance-config" element={<FinanceConfigPage />} />
        <Route path="doctor-dashboard" element={<DoctorDashboardPage />} />
        <Route path="patient-dashboard" element={<PatientDashboardPage />} />
        <Route path="service-catalog" element={<ServiceCatalogPage />} />
        <Route path="bed-status" element={<BedStatusPage />} />
        <Route path="discharge-summaries" element={<DischargeSummaryPage />} />
        <Route path="refunds" element={<RefundPage />} />
        <Route path="allergies" element={<AllergiesPage />} />
        <Route path="consultation-types" element={<ConsultationTypesPage />} />
        <Route path="doctor-availability" element={<DoctorAvailabilityPage />} />
        <Route path="vitals" element={<VitalsPage />} />
        <Route path="recurring-appointments" element={<RecurringAppointmentsPage />} />
        <Route path="reminders" element={<RemindersPage />} />
        <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
        <Route path="shifts" element={<ShiftsPage />} />
        <Route path="payroll" element={<PayrollEnhancedPage />} />
        <Route path="my-calendar" element={<DoctorCalendarPage />} />
        <Route path="audit-logs" element={<AuditLogPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<RoleLandingRedirect />} />
    </Routes>
  );
}

function App() {
  // v38.7 — Tenant bootstrap. If the SPA is loaded under a tenant subdomain
  // (e.g. prashanti.klickniramaya.com), resolve the slug to a hospital,
  // store its id in localStorage so all API calls scope to it, apply the
  // tenant's branding (title + favicon + brand colour) and flag the session
  // as "tenant-locked" so the sidebar hides the hospital selector.
  const [bootstrapping, setBootstrapping] = useState(true);
  const [bootstrapError, setBootstrapError] = useState(null);

  useEffect(() => {
    // v38.7.2 — Operator escape-hatch: when the bypass flag is set (clicked on the
    // tenant-not-configured screen), skip tenant resolution entirely for this tab so
    // the super-admin can reach the platform login and fix the hospital record.
    if (sessionStorage.getItem('bypassTenant') === '1') {
      localStorage.removeItem('tenantSlug');
      localStorage.removeItem('tenantLocked');
      setBootstrapping(false);
      return;
    }
    // v38.8 — QA / dev override: setting `sessionStorage.devTenantSlug = '<slug>'`
    // makes App.js behave AS IF the page were loaded under `<slug>.<root>`, even
    // on Emergent preview / localhost. Only used by tests; never set in production.
    const devSlug = sessionStorage.getItem('devTenantSlug');
    const slug = devSlug || detectTenantSlug();
    if (!slug) {
      localStorage.removeItem('tenantSlug');
      localStorage.removeItem('tenantLocked');
      setBootstrapping(false);
      return;
    }
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        const { data } = await axios.get(`${base}/api/tenant/by-slug/${slug}`);
        localStorage.setItem('selectedHospitalId', data.id);
        localStorage.setItem('tenantSlug', data.slug || slug);
        localStorage.setItem('tenantLocked', '1');
        localStorage.setItem('tenantName', data.name || '');
        applyTenantBranding(data);
        setBootstrapping(false);
      } catch (e) {
        // v38.7.1 — On a tenant subdomain we MUST NOT fall back to the platform login
        // (with the hospital dropdown) — that would defeat the purpose of per-tenant
        // domains in production. Show a clean error screen instead and ask the operator
        // to set the slug via Super Admin → Hospitals on the bare apex domain.
        localStorage.removeItem('tenantSlug');
        localStorage.removeItem('tenantLocked');
        setBootstrapError(slug);
        setBootstrapping(false);
        console.warn(`[Tenant Bootstrap] No hospital found for slug "${slug}"`);
      }
    })();
  }, []);

  if (bootstrapError) {
    const continueToPlatform = () => {
      // Operator escape-hatch: bypass the tenant lock for this browser tab only so the
      // super-admin can reach the platform login, create/fix the hospital record, and
      // come back to this URL. We use sessionStorage so it auto-clears on tab close.
      sessionStorage.setItem('bypassTenant', '1');
      setBootstrapError(null);
    };
    return (
      <TenantNotConfigured
        slug={bootstrapError}
        hostname={window.location.hostname}
        onContinue={continueToPlatform}
      />
    );
  }

  if (bootstrapping) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#0f172a', color: '#fff', fontFamily: 'system-ui, sans-serif',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#10b981',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
          }} />
          <p style={{ opacity: 0.7, fontSize: 13 }}>Loading hospital workspace…</p>
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
